<script lang="ts">
  import type { VizState } from '../../lib/viz';
  import { cellsOf, describeState, pairsOf } from '../../lib/labs';
  import StackBoard from './StackBoard.svelte';
  import QueueBoard from './QueueBoard.svelte';
  import DictBoard from './DictBoard.svelte';

  /** viz のフレーム 1 枚を盤面で描く（再現実行・予想の答え合わせ用、操作はできない）。 */
  interface Props {
    state: Record<string, VizState>;
    /** 描く変数（順番もこのとおり）。省略時はフレームにある全部。 */
    labels?: string[];
    /** 届いていない（フレームが無い）。 */
    missing?: boolean;
  }

  let { state, labels, missing = false }: Props = $props();

  const shown = $derived(labels ?? Object.keys(state));
  const dictLabel = $derived(shown.find((l) => state[l]?.kind === 'dict'));
  const setLabel = $derived(shown.find((l) => state[l]?.kind === 'set'));
  const others = $derived(
    shown.filter((l) => l !== dictLabel && l !== setLabel && state[l] !== undefined),
  );
</script>

{#if missing}
  <div class="missing">このステップの盤面は届いていません。</div>
{:else}
  <div class="frame">
    {#if dictLabel !== undefined || setLabel !== undefined}
      <DictBoard
        compact
        dictName={dictLabel ?? 'dict'}
        setName={setLabel ?? 'seen'}
        lockers={pairsOf(dictLabel === undefined ? undefined : state[dictLabel])}
        roster={setLabel === undefined ? null : cellsOf(state[setLabel])}
      />
    {/if}
    {#each others as l (l)}
      {@const s = state[l]}
      {#if s?.kind === 'stack'}
        <StackBoard compact name={l} items={cellsOf(s)} />
      {:else if s?.kind === 'queue'}
        <QueueBoard compact name={l} items={cellsOf(s)} head={s.head ?? 0} />
      {:else if s?.kind === 'array'}
        <div class="array">
          <span class="arr-name">{l}</span>
          <ol class="arr" aria-label="{l} の中身">
            {#each cellsOf(s) as v, i (i)}<li>{v}</li>{/each}
          </ol>
        </div>
      {:else}
        <p class="mono">{describeState(l, s)}</p>
      {/if}
    {/each}
    <p class="visually-hidden">{shown.map((l) => describeState(l, state[l])).join('。')}</p>
  </div>
{/if}

<style>
  .frame {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .missing {
    display: grid;
    place-items: center;
    min-height: 8rem;
    padding: var(--space-3);
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius-md);
    color: var(--text-soft);
    font-size: var(--fs-sm);
    text-align: center;
  }
  .array {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: var(--canvas);
    border-radius: var(--radius-md);
  }
  .arr-name,
  .mono {
    font-family: var(--font-mono);
    font-weight: var(--fw-bold);
  }
  .arr {
    display: flex;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .arr li {
    min-width: 2.2rem;
    padding: var(--space-1);
    border: 2px solid var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface);
    text-align: center;
    font-family: var(--font-mono);
    font-weight: var(--fw-bold);
  }
</style>
